import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, Grid, SelectField, Option, Link } from "@deere/fuel-react";
import SelfServiceButtons from "../SelfServiceButtons";
import { sortByLanguageList } from "../../../../utils/utils";
import { ATTR_HREF, DEFAULT_LANGUAGE_ENGLISH, EMAIL_CONTENT, LBL_BODY, LBL_BODYPARAGRAPH, LBL_TEXT_HTML, PROTOCOL_HTTP, PROTOCOL_HTTPS, PROTOCOL_MAILTO, SAVING_DATA  } from "../../../../utils/surveyLiterals";
import { validateSurveyNotification } from "../../../../utils/validation";
import { GenericFuelModal } from "../../../../components/modals/GenericFuelModal";
import RichTextEditor from "../../../../components/rich-text-editor/RichTextEditor";
import SelfServiceLoader from "../SelfServiceLoader";

const NotificationEmailStep = ({ error, setError, currentStep, activeStep, setActiveStep, payloadData, setPayloadData, languageList }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE_ENGLISH);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [emailToBePreviewed, setEmailToBePreviewed] = useState(payloadData.surveyNotification);
  const [loader, setLoader] = useState({isLoading: false, message: ""});

  useEffect(() => {
    //If a href added in the email content does not have http/https protocol
    //Then it opens up in the actual email correctly, but in the preview popup it creates issues
    // Hence adding this logic to add the protocol prefix to the email preview
    const parser = new DOMParser();
    const doc = parser.parseFromString(payloadData.surveyNotification, LBL_TEXT_HTML);
    const paraTags = doc.querySelectorAll(`p.${LBL_BODYPARAGRAPH}`);
    for (let i = 0; i < paraTags.length; i++) {
      const linkTags = paraTags[i].querySelectorAll(`a[${ATTR_HREF}]`);
      for (let j = 0; j < linkTags.length; j++) {
        const href = linkTags[j].getAttribute(ATTR_HREF);
        if (!href.startsWith(PROTOCOL_HTTP) && !href.startsWith(PROTOCOL_HTTPS) && !href.startsWith(PROTOCOL_MAILTO)) {
          linkTags[j].setAttribute(ATTR_HREF, PROTOCOL_HTTP + href);
        }
      }
    }
    setEmailToBePreviewed(doc.documentElement.outerHTML);
  }, [payloadData.surveyNotification]);

  const fnValidate = (publishHandler) => {
    const error = validateSurveyNotification(payloadData);
    error.show
      ? setError(error)
      : (setLoader({ isLoading: true, message: SAVING_DATA }), publishHandler(payloadData));
  };

  const changeHandler = (value) => {
    setError({ show: false, message: "" });
    setPayloadData((prevState) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(prevState.surveyNotification, LBL_TEXT_HTML);
      //quill editor is adding <p> tags for every line added, which is creating unnecessary margins
      //Hence, this logic will remove the unnecessary tag before storing the value in the payload
      const formattedValue = value.replace(/<p><br><\/p>/g, "<br>").replace(/<p([^>]*)>(.*?)<\/p>/g, (match, attributes, content) => {
        if (attributes.trim()) {
          return `<span${attributes}>${content}</span><br>`;
        }
        return `${content}<br>`;
      });
      const bodyElement = doc.getElementById(`${LBL_BODY}${selectedLanguage}`);
      if (bodyElement) {
        bodyElement.innerHTML = formattedValue;
      }
      return { ...prevState, surveyNotification: doc.documentElement.outerHTML };
    });
  };

  const fetchEmailvalue = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(payloadData.surveyNotification, LBL_TEXT_HTML);
    const bodyElement = doc.getElementById(`${LBL_BODY}${selectedLanguage}`);
    const value = bodyElement ? bodyElement.innerHTML : "";
    return value ? value : "";
  };

  return (
    <Card>
      <CardContent>
        {loader.message !== "" && <SelfServiceLoader loader={loader} />}
        {loader.message === "" && <Grid container direction="column" justifyContent="space-between" minHeight={"70vh"} rowSpacing={4}>
          <Grid item>
            <Typography variant="h1" mb={4}>
              {currentStep.step} - {currentStep.title}
            </Typography>
            <Typography variant="subtitle">
              This step allows you to provide the content for the email notification that will reach the suppliers working on this survey upon campaign creation.
            </Typography>
          </Grid>
          <Grid container justifyContent="flex-end"><Link href="#" onClick={() => setShowPreviewModal(true)}>Show Preview</Link></Grid>
          <Grid item>
            <SelectField label="Language" sx={{ "& .MuiFormLabel-root": { fontSize: "12px" }, marginBottom: "10px" }} value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
              {sortByLanguageList(payloadData.language, languageList).map((lang) => (
                <Option key={lang.text} value={lang.text}>
                  {lang.text}
                </Option>
              ))}
            </SelectField>
            <Typography variant="label" sx={{fontSize: "12px!important"}} fontWeight="regular">{EMAIL_CONTENT}</Typography>
            <RichTextEditor
              defaultValue={fetchEmailvalue()}
              changeHandler={changeHandler}
              valueResetDependency={selectedLanguage}
              formats1={true}
              formats2={true}
              colours={true}
              link={true}
              reset={true}
            />
          </Grid>
          {error.show && (
            <Typography variant="subtitle1" color="#C21020" textAlign="right">
              {error.message}
            </Typography>
          )}
          <Grid item>
            <Grid container justifyContent="flex-end">
              <SelfServiceButtons
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                fnValidate={fnValidate}
                payloadData={payloadData}
                setPayloadData={setPayloadData}
                setLoader={setLoader}
              />
            </Grid>
          </Grid>
        </Grid>}
      </CardContent>
      <GenericFuelModal
        title={currentStep.title}
        message={<Grid dangerouslySetInnerHTML={{ __html: emailToBePreviewed }}></Grid>}
        show={showPreviewModal}
        handleClose={() => setShowPreviewModal(false)}
        handleSave={() => setShowPreviewModal(false)}
        customStyle={{"& .MuiPaper-root": {maxWidth: "1200px"}, "& .MuiGrid-root > div": {backgroundColor: 'white!important'}}}
      />
    </Card>
  );
};

export default NotificationEmailStep;
